import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  BosDurum,
  Buton,
  DurumRozeti,
  Ikon,
  Iskelet,
  Kart,
  MetinAlani,
  Rozet,
  Secim,
  Tablo,
  anMetni,
  type UnkDurum,
} from "@koclukpanel/ui";
import { SayfaBasligiEki } from "./Kabuk";
import {
  ApiHatasi,
  KORUMALI_NOT_GEREKCE_EN_AZ,
  gorusmeArsivle,
  gorusmeDurumDegistir,
  gorusmeHakDurumu,
  gorusmeGorunurluk,
  gorusmeGuncelle,
  koclukGorusmesiAc,
  korumaliNotOku,
  korumaliNotSil,
  korumaliNotYaz,
  ogrenciGorusmeleri,
  type KoclukGorusmesi,
} from "./api";
import { useSudoKapisi } from "./SudoKapisi";
import { MeetAlani, MeetBaglantisi, meetBaglantisiHatasi } from "./MeetBaglantisi";

/**
 * KOÇLUK GÖRÜŞMELERİ — F-SES
 *
 * ================================================================
 * SİSTEMİN EN HASSAS ÖZELLİĞİ VE ARAYÜZÜ HİÇ YOKTU
 * ================================================================
 * `POST /gorusmeler`, `/durum`, `/gorunurluk` ve korumalı PDR notu
 * uçlarının hepsi sunucuda yazılıydı. Panelde hiçbiri yoktu —
 * yani uzman görüşme kaydı açamıyor, korumalı not yazamıyordu.
 *
 * ================================================================
 * KURALLARI EKRAN KOYMUYOR
 * ================================================================
 * Bu dosya korumalı nota erişim konusunda HİÇBİR karar vermiyor;
 * CLAUDE.md o üç konuda kendi kararla ilerlemeyi yasaklıyor. Ekran
 * yalnızca sunucunun söylediğini gösteriyor:
 *
 *   `izinliGecisler`        — hangi durum geçişleri mümkün
 *   `izinliGorunurlukler`   — görünürlük yalnızca DARALTILABİLİR
 *   `korumaliNotVar`        — notun VARLIĞI; içeriği değil
 *
 * Not içeriği listede HİÇ gelmiyor: ayrı bir uçtan, gerekçeyle ve
 * sudo modunda okunuyor. Ekran içeriği önbelleğe de almıyor —
 * alsaydı, "bir kez okundu, bir daha iz bırakmadan bakılabilir"
 * durumu doğardı ve her okumanın kaydedilmesi kuralı delinirdi.
 */

const TUR_ADI: Record<string, string> = {
  INTRO: "Tanışma",
  COACHING: "Koçluk",
  PARENT_MEETING: "Veli görüşmesi",
  MAKEUP: "Telafi",
};

const DURUM_ADI: Record<string, string> = {
  SCHEDULED: "Planlandı",
  REMINDED: "Hatırlatıldı",
  HELD: "Yapıldı",
  STUDENT_NO_SHOW: "Öğrenci gelmedi",
  COACH_NO_SHOW: "Uzman gelmedi",
  CANCELLED: "İptal",
  RESCHEDULED: "Ertelendi",
  AWAITING_REPORT: "Rapor bekliyor",
  REPORT_DONE: "Raporlandı",
};

const DURUM_TONU: Record<string, "notr" | "bilgi" | "onay" | "uyari" | "hata"> = {
  SCHEDULED: "bilgi",
  REMINDED: "bilgi",
  HELD: "onay",
  STUDENT_NO_SHOW: "uyari",
  COACH_NO_SHOW: "uyari",
  CANCELLED: "notr",
  RESCHEDULED: "notr",
  AWAITING_REPORT: "uyari",
  REPORT_DONE: "onay",
};

/*
  Durum tonu → UNİKOÇ dört durum rengi (tarama-2 §2.13): yapıldı/raporlandı yeşil,
  rapor bekliyor/gelmedi turuncu, planlı mavi. Gri (iptal/ertelendi) için dördüncü
  renk yok → Rozet'te kalıyor; renk uydurulmuyor.
*/
const TON_DURUMU: Partial<Record<"notr" | "bilgi" | "onay" | "uyari" | "hata", UnkDurum>> = {
  onay: "tamamlandi",
  uyari: "bekliyor",
  bilgi: "planli",
  hata: "eksik",
};

/** `Visibility` — dar olandan geniş olana. */
const GORUNURLUK_ADI: Record<string, string> = {
  PROTECTED: "Korumalı (yalnızca yazan)",
  PRIVATE: "Özel",
  INTERNAL: "Kurum içi",
  STUDENT: "Öğrenci görür",
  PARENT: "Veli görür",
  SHARED: "Öğrenci ve veli görür",
};

// Gosterim Istanbul saatiyle — tarayicinin saat dilimine birakilmaz (yurt disindaki bir koc
// ayni gorusmeyi farkli saatte gormesin). Kayit tarafi: sunucu `yerelAnSemasi` (contracts).
// Saniyesiz ("5 Ekim 2026 18:00") — ortak biçimleyici, UI/UX Dalga 1 (K11): saat
// "18:00:00" diye saniyesiyle yazılıyordu.
const zaman = (d: string): string => anMetni(d);

export function KoclukGorusmeleri({
  ogrenciId,
  ogrenciAdi,
  notYazabilir = false,
  notOkuyabilir = false,
}: {
  ogrenciId: string;
  ogrenciAdi: string;
  /** `gorusme.not_korumali.yaz` — Süper Admin'de YOK, yalnızca uzman */
  notYazabilir?: boolean;
  /** `gorusme.not_korumali.oku` — sudo listesinde, gerekçe ister */
  notOkuyabilir?: boolean;
}) {
  const [yeniAcik, setYeniAcik] = useState(false);
  const [acik, setAcik] = useState<string | null>(null);

  const liste = useQuery({
    queryKey: ["kocluk-gorusmeleri", ogrenciId],
    queryFn: () => ogrenciGorusmeleri(ogrenciId),
    retry: false,
  });

  if (yeniAcik) {
    return (
      <YeniGorusme
        ogrenciId={ogrenciId}
        ogrenciAdi={ogrenciAdi}
        onKapat={() => setYeniAcik(false)}
      />
    );
  }

  if (liste.isPending) return <Iskelet satir={4} />;

  if (liste.isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Görüşmeler alınamadı"
        aciklama={liste.error instanceof ApiHatasi ? liste.error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void liste.refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  if (liste.data.length === 0) {
    return (
      <BosDurum
        ikon="gorusme"
        baslik="Görüşme kaydı yok"
        aciklama={`${ogrenciAdi} için henüz koçluk görüşmesi açılmamış.`}
        eylem={<Buton onClick={() => setYeniAcik(true)}>Görüşme aç</Buton>}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <SayfaBasligiEki aciklama={`${ogrenciAdi} — koçluk görüşmeleri ve seans raporları.`} />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-notr-600 text-sm">{liste.data.length} görüşme</p>
        <Buton onClick={() => setYeniAcik(true)}>Görüşme aç</Buton>
      </div>

      <HakSayaci ogrenciId={ogrenciId} />

      <div className="bg-notr-0 border-mor-kenar rounded-[1.5rem] border p-2 shadow-(--golge-unk-kart) md:p-4">
        <Tablo
          baslikTuru="degrade"
          satirlar={liste.data}
          anahtar={(g) => g.id}
          kartBasligi={0}
          sutunlar={[
            { baslik: "Tarih", mobil: true, hucre: (g) => zaman(g.scheduledAt) },
            { baslik: "Tür", mobil: true, hucre: (g) => TUR_ADI[g.kind] ?? g.kind },
            {
              baslik: "Durum",
              mobil: true,
              hucre: (g) => (
                <span className="flex flex-wrap gap-1">
                  {TON_DURUMU[DURUM_TONU[g.status] ?? "notr"] ? (
                    <DurumRozeti durum={TON_DURUMU[DURUM_TONU[g.status] ?? "notr"] as UnkDurum}>
                      {DURUM_ADI[g.status] ?? g.status}
                    </DurumRozeti>
                  ) : (
                    <Rozet ton="notr">{DURUM_ADI[g.status] ?? g.status}</Rozet>
                  )}
                  {/*
                  YENİDEN PLANLANMALI — F-PPL-11b. Koçu izinde olduğu
                  için taşınması gereken görüşme.

                  Durumun YANINDA duruyor, YERİNE değil: görüşme hâlâ
                  "Planlandı" durumunda ve öyle de olmalı — kimse
                  taşımadı. İkisini birleştirseydik (durumu
                  `RESCHEDULED` yapmak) yapılmamış bir işi yapılmış
                  gibi gösterirdik.
                */}
                  {g.yenidenPlanlanmali && <Rozet ton="uyari">Yeniden planlanmalı</Rozet>}
                </span>
              ),
            },
            {
              baslik: "Korumalı not",
              mobil: true,
              hucre: (g) =>
                /*
                YALNIZCA VARLIK. İçerik burada olsaydı, listeyi açan
                herkes notu görmüş olurdu — oysa okuma gerekçe ve sudo
                istiyor ve her okuma kaydediliyor.
              */
                g.korumaliNotVar ? <Rozet ton="uyari">Var</Rozet> : null,
            },
            {
              baslik: "İşlem",
              // Telefonda da gorunur: isaretlenmeseydi `Tablo` bu
              // sutunu dar ekranda gizler ve satirin TEK islemi
              // telefondan erisilemez olurdu (07.08.2026, 375px).
              mobil: true,
              hucre: (g) => (
                <div className="flex justify-end">
                  <Buton gorunum="sessiz" onClick={() => setAcik(acik === g.id ? null : g.id)}>
                    {acik === g.id ? "Kapat" : "Aç"}
                  </Buton>
                </div>
              ),
            },
          ]}
          altSatir={(g) =>
            acik === g.id ? (
              <div className="border-notr-100 bg-notr-50 border-b px-3 py-3">
                <GorusmeAyrintisi
                  gorusme={g}
                  ogrenciId={ogrenciId}
                  notYazabilir={notYazabilir}
                  notOkuyabilir={notOkuyabilir}
                />
              </div>
            ) : null
          }
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------

function GorusmeAyrintisi({
  gorusme,
  ogrenciId,
  notYazabilir,
  notOkuyabilir,
}: {
  gorusme: KoclukGorusmesi;
  ogrenciId: string;
  notYazabilir: boolean;
  notOkuyabilir: boolean;
}) {
  const qc = useQueryClient();
  const yenile = (): void => {
    void qc.invalidateQueries({ queryKey: ["kocluk-gorusmeleri", ogrenciId] });
  };

  const durum = useMutation({
    mutationFn: (d: string) => gorusmeDurumDegistir(gorusme.id, d),
    onSuccess: yenile,
  });

  const gorunurluk = useMutation({
    mutationFn: (g: string) => gorusmeGorunurluk(gorusme.id, g),
    onSuccess: yenile,
  });

  return (
    <div className="flex flex-col gap-4">
      {/* F-SES-02b — görüşme bağlantısı: katıl + düzenle */}
      <MeetBaglantisi gorusme={gorusme} ogrenciId={ogrenciId} />

      {gorusme.agenda && (
        <p className="text-notr-700 text-sm">
          <span className="font-medium">Gündem: </span>
          {gorusme.agenda}
        </p>
      )}

      {/*
        ÖZET `null` ise iki farklı sebep olabilir: yazılmamış olabilir
        ya da OKUYUCUNUN GÖRME HAKKI olmayabilir (F-SES-04). İkisini
        aynı göstermek "özet yok" yanılgısı üretirdi; ayrımı sunucu
        `summaryVisibility` ile söylüyor.
      */}
      <Ozet gorusme={gorusme} ogrenciId={ogrenciId} />

      {/* ---- Durum ---- */}
      {gorusme.izinliGecisler.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="text-notr-800 text-sm font-medium">Durum</p>
          <div className="flex flex-wrap gap-1">
            {/*
              Hangi geçişin mümkün olduğunu SUNUCU söylüyor. Ekran
              kendi tablosunu tutsaydı, sunucuyla ayrıldığı gün
              tıklanabilir bir düğme gösterir ve sunucu reddederdi.
            */}
            {gorusme.izinliGecisler.map((d) => (
              <Buton
                key={d}
                gorunum="ikincil"
                yukleniyor={durum.isPending && durum.variables === d}
                onClick={() => durum.mutate(d)}
              >
                {DURUM_ADI[d] ?? d}
              </Buton>
            ))}
          </div>
          {durum.isError && (
            <p role="alert" className="text-hata-500 text-sm font-medium">
              {durum.error instanceof ApiHatasi ? durum.error.message : "Durum değişmedi."}
            </p>
          )}
        </div>
      )}

      {/* ---- Görünürlük ---- */}
      {gorusme.izinliGorunurlukler.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="text-notr-800 text-sm font-medium">Özet görünürlüğü</p>
          <p className="text-notr-600 text-xs">
            Şu an:{" "}
            <strong>
              {GORUNURLUK_ADI[gorusme.summaryVisibility] ?? gorusme.summaryVisibility}
            </strong>
            .
            {/*
              GENİŞLETME YOK, sunucu reddediyor. Kullanıcı bunu
              önceden bilmezse "neden listede yok" diye arar.
            */}{" "}
            Görünürlük yalnızca <strong>daraltılabilir</strong> — öğrenciye açılmış bir özet
            sonradan veliye de açılamaz, çünkü bu daraltma değil yeni bir kişiye açmaktır.
          </p>
          <div className="flex flex-wrap gap-1">
            {gorusme.izinliGorunurlukler.map((g) => (
              <Buton
                key={g}
                gorunum="sessiz"
                yukleniyor={gorunurluk.isPending && gorunurluk.variables === g}
                onClick={() => gorunurluk.mutate(g)}
              >
                {GORUNURLUK_ADI[g] ?? g}
              </Buton>
            ))}
          </div>
          {gorunurluk.isError && (
            <p role="alert" className="text-hata-500 text-sm font-medium">
              {gorunurluk.error instanceof ApiHatasi
                ? gorunurluk.error.message
                : "Görünürlük değişmedi."}
            </p>
          )}
        </div>
      )}

      <KorumaliNot
        gorusme={gorusme}
        ogrenciId={ogrenciId}
        yazabilir={notYazabilir}
        okuyabilir={notOkuyabilir}
      />

      <Arsivle gorusmeId={gorusme.id} onBitti={yenile} />
    </div>
  );
}

/**
 * GÖRÜŞME ARŞİVLEME
 *
 * `POST /gorusmeler/:id/arsivle` yazılıydı, hiçbir ekran
 * çağırmıyordu: yanlışlıkla açılmış ya da gerçekleşmemiş bir görüşme
 * kaydı listede kalıcı olarak duruyordu.
 *
 * ================================================================
 * KALICI SİLME YOK — GÖRÜNEN "SİL" DEĞİL "ARŞİVLE"
 * ================================================================
 * Düğme "Sil" yazsaydı kullanıcı kaydın gittiğini sanardı; kayıt
 * duruyor ve korumalı not da duruyor. İzni olmayan 403 alır —
 * düğmeyi gizlemek yerine sunucunun cümlesini göstermek, "bu ekran
 * bende eksik" izleniminden iyi.
 */
function Arsivle({ gorusmeId, onBitti }: { gorusmeId: string; onBitti: () => void }) {
  const [onay, setOnay] = useState(false);

  const mut = useMutation({
    mutationFn: () => gorusmeArsivle(gorusmeId),
    onSuccess: () => {
      setOnay(false);
      onBitti();
    },
  });

  if (!onay) {
    return (
      <div className="border-notr-200 border-t pt-3">
        <Buton gorunum="sessiz" onClick={() => setOnay(true)}>
          Görüşmeyi arşivle
        </Buton>
      </div>
    );
  }

  return (
    <div className="rounded-alan border-notr-200 flex flex-col gap-2 border p-3">
      <p className="text-notr-700 text-sm">
        Kayıt <strong>arşivlenir, silinmez</strong>: listelerden düşer, korumalı not ve denetim
        kaydı yerinde kalır.
      </p>
      {mut.isError && (
        <p role="alert" className="text-hata-700 text-sm font-medium">
          {mut.error instanceof ApiHatasi ? mut.error.message : "Arşivlenemedi."}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Buton gorunum="tehlike" yukleniyor={mut.isPending} onClick={() => mut.mutate()}>
          Arşivle
        </Buton>
        <Buton gorunum="ikincil" onClick={() => setOnay(false)}>
          Vazgeç
        </Buton>
      </div>
    </div>
  );
}

function Ozet({ gorusme, ogrenciId }: { gorusme: KoclukGorusmesi; ogrenciId: string }) {
  const qc = useQueryClient();
  const [duzenle, setDuzenle] = useState(false);
  const [metin, setMetin] = useState(gorusme.summary ?? "");

  const kaydet = useMutation({
    mutationFn: () => gorusmeGuncelle(gorusme.id, { summary: metin.trim() || null }),
    onSuccess: async () => {
      setDuzenle(false);
      await qc.invalidateQueries({ queryKey: ["kocluk-gorusmeleri", ogrenciId] });
    },
  });

  if (duzenle) {
    return (
      <div className="flex flex-col gap-2">
        <MetinAlani
          etiket="Görüşme özeti"
          rows={5}
          value={metin}
          onChange={(e) => setMetin(e.target.value)}
          yardim={`Görünürlük: ${GORUNURLUK_ADI[gorusme.summaryVisibility] ?? gorusme.summaryVisibility}. Bu özet korumalı PDR notu DEĞİLDİR.`}
        />
        {kaydet.isError && (
          <p role="alert" className="text-hata-500 text-sm font-medium">
            {kaydet.error instanceof ApiHatasi ? kaydet.error.message : "Kaydedilemedi."}
          </p>
        )}
        <div className="flex gap-2">
          <Buton yukleniyor={kaydet.isPending} onClick={() => kaydet.mutate()}>
            Kaydet
          </Buton>
          <Buton gorunum="ikincil" onClick={() => setDuzenle(false)}>
            Vazgeç
          </Buton>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <p className="text-notr-800 text-sm font-medium">Özet</p>
      {gorusme.summary === null ? (
        <p className="text-notr-600 text-sm">
          {/*
            "Özet yok" DEMİYORUZ. Sunucu görme hakkı olmayana `null`
            döndürüyor (F-SES-04) ve "yok" demek yanlış bilgi olurdu.
          */}
          Özet görüntülenemiyor — yazılmamış olabilir ya da bu özet size kapalı olabilir.
        </p>
      ) : (
        <p className="text-notr-700 text-sm whitespace-pre-wrap">{gorusme.summary}</p>
      )}
      <div>
        <Buton gorunum="sessiz" onClick={() => setDuzenle(true)}>
          {gorusme.summary === null ? "Özet yaz" : "Özeti düzenle"}
        </Buton>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------

/**
 * KORUMALI PDR NOTU — F-SES-05
 *
 * ================================================================
 * OKUNAN NOT ÖNBELLEĞE ALINMIYOR
 * ================================================================
 * Not okunduktan sonra bileşen durumunda tutuluyor ama sorgu
 * önbelleğine KONULMUYOR ve panel kapanınca gidiyor. Önbelleğe
 * alınsaydı ikinci okuma erişim tablosuna yazılmaz, "her okuma
 * kaydedilir" kuralı sessizce delinirdi.
 */
function KorumaliNot({
  gorusme,
  ogrenciId,
  yazabilir,
  okuyabilir,
}: {
  gorusme: KoclukGorusmesi;
  ogrenciId: string;
  yazabilir: boolean;
  okuyabilir: boolean;
}) {
  const qc = useQueryClient();
  const [gerekce, setGerekce] = useState("");
  const [okunan, setOkunan] = useState<string | null>(null);
  const [yaziliyor, setYaziliyor] = useState(false);
  const [yeniNot, setYeniNot] = useState("");

  const { hataYakala, kapi } = useSudoKapisi(
    "Korumalı PDR notu özel nitelikli veridir; okumadan önce kimliğinizi doğrulayın.",
  );

  const oku = useMutation({
    mutationFn: () => korumaliNotOku(gorusme.id, gerekce.trim()),
    onSuccess: (s) => setOkunan(s.not),
    onError: hataYakala(() => oku.mutate()),
  });

  const yaz = useMutation({
    mutationFn: () => korumaliNotYaz(gorusme.id, yeniNot.trim()),
    onSuccess: async () => {
      setYeniNot("");
      setYaziliyor(false);
      await qc.invalidateQueries({ queryKey: ["kocluk-gorusmeleri", ogrenciId] });
    },
    onError: hataYakala(() => yaz.mutate()),
  });

  const sil = useMutation({
    mutationFn: () => korumaliNotSil(gorusme.id),
    onSuccess: async () => {
      setOkunan(null);
      await qc.invalidateQueries({ queryKey: ["kocluk-gorusmeleri", ogrenciId] });
    },
    onError: hataYakala(() => sil.mutate()),
  });

  if (!yazabilir && !okuyabilir) return null;

  return (
    <div className="rounded-kart shadow-kart border-uyari-500/30 bg-uyari-500/5 flex flex-col gap-3 border p-3">
      {kapi}

      <div className="flex items-start gap-2">
        <Ikon ad="kvkk" className="text-uyari-600 mt-0.5" />
        <div>
          <p className="text-notr-900 text-sm font-medium">Korumalı PDR notu</p>
          <p className="text-notr-600 text-xs">
            Özel nitelikli veri. Ayrı anahtarla şifrelenir, veli ve öğrenciye HİÇBİR koşulda
            açılmaz. <strong>Her okuma kaydedilir</strong> — Süper Admin dahil kimse iz bırakmadan
            okuyamaz.
          </p>
        </div>
      </div>

      {!gorusme.korumaliNotVar && !yaziliyor && (
        <p className="text-notr-600 text-sm">Bu görüşmede korumalı not yok.</p>
      )}

      {/* ---- OKUMA ---- */}
      {gorusme.korumaliNotVar && okuyabilir && okunan === null && (
        <div className="flex flex-col gap-2">
          <Alan
            etiket="Erişim gerekçesi"
            value={gerekce}
            onChange={(e) => setGerekce(e.target.value)}
            /*
              ALT SINIR EKRANDA YAZILI. Sunucu 15 karakterden kısa
              gerekçeyi reddediyor; yazmasaydık kullanıcı "x" yazıp
              gönderir ve ancak reddedilince öğrenirdi.
            */
            yardim={`En az ${KORUMALI_NOT_GEREKCE_EN_AZ} karakter. Ne aradığınızı açıklayın; bu metin erişim kaydına yazılır.`}
            hata={
              gerekce.trim().length > 0 && gerekce.trim().length < KORUMALI_NOT_GEREKCE_EN_AZ
                ? `${KORUMALI_NOT_GEREKCE_EN_AZ - gerekce.trim().length} karakter daha gerekiyor.`
                : undefined
            }
          />
          {oku.isError && (
            <p role="alert" className="text-hata-500 text-sm font-medium">
              {oku.error instanceof ApiHatasi ? oku.error.message : "Not okunamadı."}
            </p>
          )}
          <div>
            <Buton
              yukleniyor={oku.isPending}
              disabled={gerekce.trim().length < KORUMALI_NOT_GEREKCE_EN_AZ}
              onClick={() => oku.mutate()}
            >
              Gerekçeyle oku
            </Buton>
          </div>
        </div>
      )}

      {okunan !== null && (
        <div className="flex flex-col gap-2">
          <p className="rounded-alan bg-notr-0 text-notr-800 ring-notr-200 p-3 text-sm whitespace-pre-wrap ring-1">
            {okunan}
          </p>
          <div className="flex flex-wrap gap-2">
            {/*
              KAPATMAK notu ekrandan siliyor. Tekrar bakmak yeni bir
              gerekçe ve yeni bir kayıt demek — kural bu.
            */}
            <Buton
              gorunum="ikincil"
              onClick={() => {
                setOkunan(null);
                setGerekce("");
              }}
            >
              Kapat
            </Buton>
            {yazabilir && (
              <Buton gorunum="tehlike" yukleniyor={sil.isPending} onClick={() => sil.mutate()}>
                Notu sil
              </Buton>
            )}
          </div>
          {yazabilir && (
            <p className="text-notr-600 text-xs">
              Silme geri alınamaz: içerik gerçekten silinir, &quot;silindi&quot; işaretiyle
              saklanmaz.
            </p>
          )}
        </div>
      )}

      {gorusme.korumaliNotVar && !okuyabilir && (
        <p className="text-notr-600 text-sm">
          Bu notu okuma yetkiniz yok. Yetkisi olan biri okusa bile bu erişim kayda geçer.
        </p>
      )}

      {/* ---- YAZMA ---- */}
      {yazabilir && !gorusme.korumaliNotVar && (
        <div className="flex flex-col gap-2">
          {yaziliyor ? (
            <>
              <MetinAlani
                etiket="Korumalı not"
                rows={6}
                value={yeniNot}
                onChange={(e) => setYeniNot(e.target.value)}
                yardim="Öğrencinin açık rızası yoksa sunucu reddeder."
              />
              {yaz.isError && (
                <p role="alert" className="text-hata-500 text-sm font-medium">
                  {yaz.error instanceof ApiHatasi ? yaz.error.message : "Not yazılamadı."}
                </p>
              )}
              <div className="flex gap-2">
                <Buton
                  yukleniyor={yaz.isPending}
                  disabled={yeniNot.trim().length === 0}
                  onClick={() => yaz.mutate()}
                >
                  Notu kaydet
                </Buton>
                <Buton gorunum="ikincil" onClick={() => setYaziliyor(false)}>
                  Vazgeç
                </Buton>
              </div>
            </>
          ) : (
            <div>
              <Buton gorunum="ikincil" onClick={() => setYaziliyor(true)}>
                Korumalı not yaz
              </Buton>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------

function YeniGorusme({
  ogrenciId,
  ogrenciAdi,
  onKapat,
}: {
  ogrenciId: string;
  ogrenciAdi: string;
  onKapat: () => void;
}) {
  const qc = useQueryClient();
  const [tur, setTur] = useState("COACHING");
  const [ne, setNe] = useState("");
  const [sure, setSure] = useState("45");
  const [gundem, setGundem] = useState("");
  // F-SES-02b — Meet bağlantısı (isteğe bağlı, yalnız https).
  const [meet, setMeet] = useState("");
  const meetHatasi = meetBaglantisiHatasi(meet);

  const ac = useMutation({
    mutationFn: () =>
      koclukGorusmesiAc({
        ogrenciId,
        kind: tur,
        scheduledAt: ne,
        durationMin: sure.trim() ? Number(sure) : null,
        meetUrl: meet.trim() || null,
        agenda: gundem.trim() || null,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["kocluk-gorusmeleri", ogrenciId] });
      onKapat();
    },
  });

  return (
    <Kart baslik={`${ogrenciAdi} — görüşme aç`}>
      <div className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Secim etiket="Tür" value={tur} onChange={(e) => setTur(e.target.value)}>
            {Object.entries(TUR_ADI).map(([d, ad]) => (
              <option key={d} value={d}>
                {ad}
              </option>
            ))}
          </Secim>
          <Alan
            etiket="Tarih ve saat"
            type="datetime-local"
            value={ne}
            onChange={(e) => setNe(e.target.value)}
          />
          <Alan
            etiket="Süre (dakika)"
            type="number"
            min={1}
            value={sure}
            onChange={(e) => setSure(e.target.value)}
          />
        </div>

        <MeetAlani deger={meet} onDegisti={setMeet} hata={meetHatasi} />

        <MetinAlani
          etiket="Gündem"
          rows={3}
          value={gundem}
          onChange={(e) => setGundem(e.target.value)}
          yardim="İsteğe bağlı."
        />

        {ac.isError && (
          <p role="alert" className="text-hata-500 text-sm font-medium">
            {ac.error instanceof ApiHatasi ? ac.error.message : "Görüşme açılamadı."}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <Buton
            yukleniyor={ac.isPending}
            disabled={ne.length === 0 || meetHatasi !== null}
            onClick={() => ac.mutate()}
          >
            Görüşmeyi aç
          </Buton>
          <Buton gorunum="ikincil" onClick={onKapat}>
            Vazgeç
          </Buton>
        </div>
      </div>
    </Kart>
  );
}

/**
 * PAKET GÖRÜŞME HAKKI SAYACI — F-SES-07
 *
 * `GET /gorusmeler/ogrenci/:id/hak` yazılıydı, hiçbir ekran
 * çağırmıyordu.
 *
 * ================================================================
 * TOPLAM VE KALAN ŞU AN `null` — VE BU BİR ARIZA DEĞİL
 * ================================================================
 * Sunucunun kendi notu: "toplam ve kalan Faz 5'te (paket tanımı)
 * gelecek; şimdilik null döner." Paket artık tanımlanabiliyor ama
 * görüşme hakkı sayısı henüz pakete bağlanmadı.
 *
 * Ekran `null`ı "0 hak kaldı" diye GÖSTERMİYOR. Gösterseydi uzman
 * hakkı bitmiş sanıp görüşme açmaktan kaçınırdı — olmayan bir
 * kısıtın etkisini yaratmak, kısıtın kendisinden kötü.
 *
 * KULLANILAN gerçek bir sayı ve gösteriliyor: hakkın kaçının
 * harcandığı bugün de bilinebilir.
 */
function HakSayaci({ ogrenciId }: { ogrenciId: string }) {
  const { data, isError } = useQuery({
    queryKey: ["gorusme-hak", ogrenciId],
    queryFn: () => gorusmeHakDurumu(ogrenciId),
    retry: false,
  });

  // Yetkisi olmayana hicbir sey gosterilmiyor: "hak bilgisi alinamadi"
  // demek, olmayan bir arizayi haber vermek olurdu.
  if (isError || !data) return null;

  return (
    <p className="rounded-alan bg-notr-50 text-notr-700 px-3 py-2 text-sm">
      Bu öğrenci için <strong>{data.kullanilan}</strong> görüşme hakkı kullanıldı.
      {data.toplam === null ? (
        <span className="text-notr-600 block text-xs">
          Paket görüşme hakkı henüz tanımlanmadı; kalan hak sayısı gösterilemiyor. Bu bir kısıt
          olmadığı anlamına gelir — görüşme açmayı engellemez.
        </span>
      ) : (
        <span className="text-notr-600 block text-xs">
          Paket hakkı {data.toplam}, kalan {data.kalan}.
        </span>
      )}
    </p>
  );
}
